/**
 * Shared downward-dismiss interaction for mobile sheets. The sheet owns its
 * data and visibility state; this helper only follows a deliberate downward
 * pull and calls the supplied dismissal callback after its exit animation.
 */
import { SHEET_CLICK_SUPPRESS_MS, SHEET_EDGE_PULL_ARM_PX, SHEET_REJECT_HORIZONTAL_PX, SHEET_SETTLE_MS, shouldDismissSheet } from './sheetDismissPolicy';

export type SheetDismissGestureOptions = { chromeSelector: string; scrollSelector: string; closeSelector?: string };
type DragState = {
	pointerId: number;
	startX: number;
	startY: number;
	lastY: number;
	lastT: number;
	velocity: number;
	source: Element | null;
};
type ListPullState = {
	identifier: number;
	startX: number;
	startY: number;
	source: Element | null;
	scrollRegion: HTMLElement;
	edgeStartY: number | null;
	horizontal: boolean;
};
const attached = new WeakMap<HTMLElement, () => void>();

export function attachSheetDismissGesture(sheet: HTMLElement, dismiss: () => void, options: SheetDismissGestureOptions): void {
	if (attached.has(sheet)) return;
	attached.set(sheet, registerGesture(sheet, dismiss, options));
}

function registerGesture(sheet: HTMLElement, dismiss: () => void, options: SheetDismissGestureOptions): () => void {
	const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	let drag: DragState | null = null;
	let listPull: ListPullState | null = null;
	let settling = false;
	let settleTimer: number | null = null;
	let suppressClickUntil = 0;
	let suppressClickSource: Element | null = null;
	const setTransform = (value: string) => { sheet.style.transform = value; };
	const clearSettle = () => {
		if (settleTimer !== null) window.clearTimeout(settleTimer);
		settleTimer = null;
		settling = false;
		sheet.style.transition = '';
	};
	const settleTo = (target: string, onSettled?: () => void) => {
		clearSettle();
		if (reducedMotion) {
			setTransform(target);
			onSettled?.();
			return;
		}
		settling = true;
		sheet.style.transition = `transform ${SHEET_SETTLE_MS}ms cubic-bezier(0.2, 0.7, 0.3, 1)`;
		setTransform(target);
		settleTimer = window.setTimeout(() => {
			settleTimer = null;
			settling = false;
			sheet.style.transition = '';
			onSettled?.();
		}, SHEET_SETTLE_MS);
	};
	const settleBack = () => {
		drag = null;
		settleTo('');
	};
	const startDrag = (pointerId: number, x: number, y: number, source: Element | null) => {
		clearSettle();
		drag = { pointerId, startX: x, startY: y, lastY: y, lastT: performance.now(), velocity: 0, source };
		sheet.style.transition = 'none';
	};
	const trackVelocity = (y: number) => {
		if (!drag) return;
		const now = performance.now();
		const elapsed = now - drag.lastT;
		if (elapsed > 0) drag.velocity = (y - drag.lastY) / elapsed;
		drag.lastY = y;
		drag.lastT = now;
	};
	const followDrag = (y: number) => {
		if (!drag) return;
		const dy = y - drag.startY;
		setTransform(dy > 0 ? `translateY(${Math.round(dy)}px)` : '');
	};
	const suppressSyntheticClick = (source: Element | null) => {
		suppressClickUntil = performance.now() + SHEET_CLICK_SUPPRESS_MS;
		suppressClickSource = source;
	};
	const finishDrag = ({ canceled = false, horizontal = false, endY }: { canceled?: boolean; horizontal?: boolean; endY?: number } = {}) => {
		if (!drag) return;
		if (endY !== undefined) trackVelocity(endY);
		const active = drag;
		const dy = Math.max(0, (endY ?? active.lastY) - active.startY);
		drag = null;
		if (!canceled && !horizontal && dy > SHEET_EDGE_PULL_ARM_PX) suppressSyntheticClick(active.source);
		if (!shouldDismissSheet({ canceled, horizontal, dy, velocity: active.velocity })) {
			settleTo('');
			return;
		}
		settleTo('translateY(100%)', () => {
			setTransform('');
			dismiss();
		});
	};
	const sourceFor = (target: Element | null) => target?.closest('button, [data-proto-click]') ?? target;
	const onPointerDown = (event: PointerEvent) => {
		const target = event.target instanceof Element ? event.target : null;
		if (settling || !target || !target.closest(options.chromeSelector)) return;
		// A pull may start on the close control itself. Touch keeps its implicit
		// capture on the button so moves still bubble through the sheet; claiming
		// capture here would retarget the release away from the button and break
		// tap-to-close, so mouse pointers keep their plain click shortcut.
		const fromClose = Boolean(target.closest(options.closeSelector ?? '.modal-close'));
		if (fromClose && event.pointerType !== 'touch') return;
		startDrag(event.pointerId, event.clientX, event.clientY, sourceFor(target));
		if (!fromClose) {
			try { sheet.setPointerCapture(event.pointerId); } catch { /* best effort */ }
		}
	};
	const onPointerMove = (event: PointerEvent) => {
		if (!drag || drag.pointerId !== event.pointerId) return;
		const dx = event.clientX - drag.startX;
		const dy = event.clientY - drag.startY;
		if (Math.abs(dx) > SHEET_REJECT_HORIZONTAL_PX && Math.abs(dx) > Math.abs(dy)) {
			finishDrag({ horizontal: true, endY: event.clientY });
			return;
		}
		trackVelocity(event.clientY);
		followDrag(event.clientY);
	};
	const onPointerEnd = (event: PointerEvent) => {
		if (drag?.pointerId === event.pointerId) finishDrag({ endY: event.clientY });
	};
	const onPointerCancel = (event: PointerEvent) => {
		if (drag?.pointerId === event.pointerId) finishDrag({ canceled: true });
	};
	const touchFor = (touches: TouchList, identifier: number) => Array.from(touches).find((touch) => touch.identifier === identifier) ?? null;
	const clearListPull = () => {
		listPull = null;
		window.removeEventListener('touchmove', onTouchMove);
		window.removeEventListener('touchend', onTouchEnd);
		window.removeEventListener('touchcancel', onTouchCancel);
	};
	const onTouchStart = (event: TouchEvent) => {
		const target = event.target instanceof Element ? event.target : null;
		const list = sheet.querySelector<HTMLElement>(options.scrollSelector);
		const touch = event.touches[0];
		if (settling || !target || !list || !list.contains(target) || !touch) return;
		clearListPull();
		listPull = {
			identifier: touch.identifier,
			startX: touch.clientX,
			startY: touch.clientY,
			source: sourceFor(target),
			scrollRegion: list,
			edgeStartY: list.scrollTop <= 0 ? touch.clientY : null,
			horizontal: false,
		};
		window.addEventListener('touchmove', onTouchMove, { passive: false });
		window.addEventListener('touchend', onTouchEnd);
		window.addEventListener('touchcancel', onTouchCancel);
	};
	const onTouchMove = (event: TouchEvent) => {
		const pull = listPull;
		if (!pull) return;
		const touch = touchFor(event.touches, pull.identifier) ?? touchFor(event.changedTouches, pull.identifier);
		if (!touch) return;
		const dx = touch.clientX - pull.startX;
		const dy = touch.clientY - pull.startY;
		if (Math.abs(dx) > SHEET_REJECT_HORIZONTAL_PX && Math.abs(dx) > Math.abs(dy)) {
			pull.horizontal = true;
			clearListPull();
			settleBack();
			return;
		}
		if (!drag) {
			if (pull.scrollRegion.scrollTop > 0) {
				// Native pan-y keeps ownership until the list reaches its first row.
				pull.edgeStartY = null;
				return;
			}
			if (pull.edgeStartY === null) {
				// Keep the original touch sequence. Once native scrolling reaches the
				// edge, this same pull can become a dismissal without a second swipe.
				pull.edgeStartY = touch.clientY;
			}
			const edgeDy = touch.clientY - pull.edgeStartY;
			if (edgeDy <= SHEET_EDGE_PULL_ARM_PX) return;
			startDrag(-1, pull.startX, pull.edgeStartY, pull.source);
		}
		event.preventDefault();
		trackVelocity(touch.clientY);
		followDrag(touch.clientY);
	};
	const onTouchEnd = (event: TouchEvent) => {
		const pull = listPull;
		if (!pull) return;
		const touch = touchFor(event.changedTouches, pull.identifier);
		clearListPull();
		if (drag?.pointerId === -1) finishDrag({ horizontal: pull.horizontal, endY: touch?.clientY });
	};
	const onTouchCancel = () => {
		const wasTouchDrag = drag?.pointerId === -1;
		clearListPull();
		if (wasTouchDrag) finishDrag({ canceled: true });
	};
	const onClick = (event: MouseEvent) => {
		if (performance.now() >= suppressClickUntil) {
			suppressClickUntil = 0;
			suppressClickSource = null;
			return;
		}
		const target = event.target instanceof Element ? event.target : null;
		if (!target || !suppressClickSource?.contains(target)) return;
		event.preventDefault();
		event.stopPropagation();
	};
	sheet.addEventListener('pointerdown', onPointerDown);
	sheet.addEventListener('pointermove', onPointerMove);
	sheet.addEventListener('pointerup', onPointerEnd);
	sheet.addEventListener('pointercancel', onPointerCancel);
	sheet.addEventListener('touchstart', onTouchStart, { passive: true });
	sheet.addEventListener('click', onClick, true);
	return () => {
		clearSettle();
		clearListPull();
		sheet.removeEventListener('pointerdown', onPointerDown);
		sheet.removeEventListener('pointermove', onPointerMove);
		sheet.removeEventListener('pointerup', onPointerEnd);
		sheet.removeEventListener('pointercancel', onPointerCancel);
		sheet.removeEventListener('touchstart', onTouchStart);
		sheet.removeEventListener('click', onClick, true);
		setTransform('');
		sheet.style.transition = '';
	};
}
